import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../utils/Layouts/Layout/Layout";
import Axios from "../../Axios";
import Header from "../../utils/Header/Header";
import "./news.scss";

const noIMG = require("../../assets/no-image.png");
const banner = require("../../assets/medee_banner.png");

const PAGE_SIZE = 6;

const stripHtml = (html = "") => {
  if (!html) return "";
  const temp = document.createElement("div");
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || "";
};

const truncateText = (text = "", maxLength = 180) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}...`;
};

const formatDate = (dateValue) => {
  if (!dateValue) return "Шинэ мэдээ";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "Шинэ мэдээ";

  return new Intl.DateTimeFormat("mn-MN", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
};

const normalizeCategory = (item) => {
  return (
    item?.categoryName ||
    item?.category ||
    item?.type ||
    item?.newsType ||
    "Бусад"
  );
};

const NewsSkeletonCard = ({ featured = false }) => {
  return (
    <div
      className={`news-skeleton ${featured ? "news-skeleton--featured" : ""}`}
    >
      <div className="news-skeleton__image" />
      <div className="news-skeleton__body">
        <div className="news-skeleton__meta" />
        <div className="news-skeleton__title" />
        <div className="news-skeleton__title short" />
        <div className="news-skeleton__text" />
        <div className="news-skeleton__text" />
        <div className="news-skeleton__text short" />
      </div>
    </div>
  );
};

const NewsCard = ({ item, featured = false }) => {
  return (
    <article className={`news-card ${featured ? "news-card--featured" : ""}`}>
      <Link to={`/news/${item.id}`} className="news-card__imageWrap">
        <img
          src={item.cover || noIMG}
          alt={item.title || "news cover"}
          className="news-card__image"
        />
        <div className="news-card__imageOverlay" />
      </Link>

      <div className="news-card__body">
        <div className="news-card__meta">
          <span className="news-card__badge">{item.category}</span>
          <span className="news-card__date">{item.formattedDate}</span>
        </div>

        <h3 className="news-card__title">
          <Link to={`/news/${item.id}`}>{item.title}</Link>
        </h3>

        <p className="news-card__text">{item.preview}</p>

        <Link to={`/news/${item.id}`} className="news-card__link">
          Дэлгэрэнгүй <span>→</span>
        </Link>
      </div>
    </article>
  );
};

const News = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("Бүгд");
  const [searchText, setSearchText] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setLoading(true);

    Axios.get("/news/list")
      .then((res) => {
        if (res.status === 200) {
          setData(Array.isArray(res.data) ? res.data : []);
        } else {
          setData([]);
        }
      })
      .catch(() => {
        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const newsList = useMemo(() => {
    return (Array.isArray(data) ? data : []).map((item) => ({
      ...item,
      category: normalizeCategory(item),
      preview: truncateText(stripHtml(item?.content ?? ""), 220),
      searchableText:
        `${item?.title || ""} ${stripHtml(item?.content ?? "")}`.toLowerCase(),
      formattedDate: formatDate(
        item?.createdAt || item?.date || item?.created_at,
      ),
    }));
  }, [data]);

  const categories = useMemo(() => {
    const unique = Array.from(
      new Set(newsList.map((item) => item.category).filter(Boolean)),
    );
    return ["Бүгд", ...unique];
  }, [newsList]);

  const filteredNews = useMemo(() => {
    let list = [...newsList];

    if (activeCategory !== "Бүгд") {
      list = list.filter((item) => item.category === activeCategory);
    }

    if (searchText.trim()) {
      const q = searchText.toLowerCase().trim();
      list = list.filter((item) => item.searchableText.includes(q));
    }

    return list;
  }, [newsList, activeCategory, searchText]);

  const featuredNews =
    !searchText.trim() && activeCategory === "Бүгд" ? filteredNews[0] : null;

  const normalNews = featuredNews
    ? filteredNews.slice(1, visibleCount + 1)
    : filteredNews.slice(0, visibleCount);

  const hasMore = featuredNews
    ? filteredNews.length - 1 > normalNews.length
    : filteredNews.length > normalNews.length;

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeCategory, searchText]);

  return (
    <Layout
      banner={
        <section className="news-hero">
          <img className="news-hero__image" src={banner} alt="news banner" />
          {/* <div className="news-hero__overlay" />
          <div className="news-hero__content">
            <span className="news-hero__badge">Мэдээ мэдээлэл</span>
            <h1 className="news-hero__title">Сүүлийн үеийн онцлох мэдээнүүд</h1>
            <p className="news-hero__desc">
              Шинэ мэдээ, үйл явдал, нийтлэл, онцлох мэдээллийг нэг дороос.
            </p>
          </div> */}
        </section>
      }
    >
      <Header onHome={true} />

      <section className="news-page">
        <div className="news-page__header">
          <div>
            <h2 className="news-page__title">Бүх мэдээ</h2>
          </div>

          <div className="news-page__stats">
            Нийт <strong>{filteredNews.length}</strong> мэдээ
          </div>
        </div>

        <div className="news-toolbar">
          <div className="news-search">
            <span className="news-search__icon">⌕</span>
            <input
              type="text"
              placeholder="Мэдээ хайх..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {searchText ? (
              <button
                type="button"
                className="news-search__clear"
                onClick={() => setSearchText("")}
              >
                ✕
              </button>
            ) : null}
          </div>

          <div className="news-tabs">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`news-tabs__item ${activeCategory === cat ? "active" : ""}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <>
            <div className="news-featured">
              <NewsSkeletonCard featured />
            </div>
            <div className="news-grid">
              {Array.from({ length: 6 }).map((_, index) => (
                <NewsSkeletonCard key={index} />
              ))}
            </div>
          </>
        ) : filteredNews.length > 0 ? (
          <>
            {featuredNews ? (
              <div className="news-featured">
                <NewsCard item={featuredNews} featured />
              </div>
            ) : null}

            <div className="news-grid">
              {normalNews.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>

            {hasMore ? (
              <div className="news-loadmore">
                <button
                  type="button"
                  className="news-loadmore__btn"
                  onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                >
                  Цааш үзэх
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <div className="news-empty">
            <div className="news-empty__icon">📰</div>
            <h3>Илэрц олдсонгүй</h3>
            <p>Таны хайлт эсвэл ангилалд тохирох мэдээ олдсонгүй.</p>
          </div>
        )}
      </section>
    </Layout>
  );
};

export default News;
